import { defineResource, hues } from '@tnn/core'
import { z } from 'zod'
import { addDays } from './attendance'
import { departmentStatuses } from './resources'

/**
 * Leave (การลา): kinds of leave with how many days a year each gives, per-person adjustments,
 * and the leave itself — asked for in Thananan OS (or recorded by a lead for someone without a
 * phone), approved by the person's manager, their branch's lead or HR, never by themselves.
 */

/** Icons a kind of leave may wear (drawn by `LeaveTypeIcon` in @tnn/people) */
export const leaveIcons = [
  'calendar',
  'firstAid',
  'stethoscope',
  'briefcase',
  'beach',
  'plane',
  'baby',
  'shield',
  'cashOff',
  'home',
  'heart',
  'school',
  'pray',
  'flower',
  'users',
  'cake',
] as const
export type LeaveIcon = (typeof leaveIcons)[number]

/**
 * hr.leaveType (ประเภทการลา). The defaults follow the Labour Protection Act (`standardLeaveTypes`);
 * HR changes the days and adds the company's own (ordination…). Each wears its own icon and colour
 * wherever leave of its kind shows (ตารางเวลาทำงาน, ใบลา, วันลาคงเหลือ…).
 */
export const leaveType = defineResource({
  app: 'hr',
  name: 'leaveType',
  path: 'leave-types',
  schema: z.object({
    code: z.string().trim().toUpperCase().min(1).max(20),
    name: z.string().trim().min(1).max(60),
    /** With pay (sick, personal, annual) or without */
    paid: z.boolean().default(true),
    /** Days a year; empty: no limit (unpaid leave) */
    daysPerYear: z.number().min(0).max(366).nullish(),
    /** Counts every day, holidays included (maternity: 98 days), rather than working days */
    calendarDays: z.boolean().default(false),
    /** Service needed first (annual leave: one year → 365) */
    minServiceDays: z.number().int().min(0).max(3650).default(0),
    /**
     * In the year someone first qualifies (their start + minServiceDays), the days by the months
     * left in it (annual leave: qualifying late in October → 6 × 2/12 = 1 day); full from the next year
     */
    prorate: z.boolean().default(false),
    /** From this many days in one leave, a document is needed (sick leave: a medical certificate from 3 days) */
    documentFromDays: z.number().int().min(1).max(366).nullish(),
    /** May be taken by the half day */
    halfDay: z.boolean().default(true),
    /** Asked for at least this many days ahead (0: today is fine) */
    noticeDays: z.number().int().min(0).max(90).default(0),
    /** May be asked for days already past (sick leave, written on coming back) */
    allowPast: z.boolean().default(false),
    /** Taking it keeps the month's diligence allowance (annual leave); any other leave loses it */
    keepsDiligence: z.boolean().default(false),
    icon: z.enum(leaveIcons).default('calendar'),
    color: z.enum(hues).default('blue'),
    status: z.enum(departmentStatuses).default('active'),
    sortOrder: z.number().int().min(0).max(999).default(100),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    code: { search: true, list: { width: 110, priority: 2 }, form: { span: 4 } },
    name: { search: true, list: { priority: 1 }, form: { span: 8 } },
    icon: { list: false, form: { span: 6, custom: true } },
    color: { list: false, form: { span: 6, custom: true } },
    paid: { list: { width: 110, priority: 1 }, form: { span: 4 } },
    daysPerYear: { list: { width: 120, priority: 1 }, form: { span: 4 } },
    calendarDays: { list: false, form: { span: 4 } },
    minServiceDays: { list: { priority: 3, hidden: true }, form: { span: 4, section: 'rules' } },
    prorate: { list: false, form: { span: 4, section: 'rules' } },
    documentFromDays: { list: { priority: 3, hidden: true }, form: { span: 4, section: 'rules' } },
    noticeDays: { list: false, form: { span: 4, section: 'rules' } },
    halfDay: { list: false, form: { span: 4, section: 'rules' } },
    allowPast: { list: false, form: { span: 4, section: 'rules' } },
    keepsDiligence: { list: { width: 130, priority: 3, hidden: true }, form: { span: 4, section: 'rules' } },
    status: { filter: true, tones: { active: 'success', inactive: 'neutral' }, list: { width: 120, priority: 1 }, form: { span: 4 } },
    sortOrder: { list: false, form: { span: 4 } },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'rules' } },
  },
  title: 'name',
  columns: ['name', 'code', 'paid', 'daysPerYear', 'minServiceDays', 'documentFromDays', 'status'],
  sections: ['main', 'rules'],
  defaultSort: 'sortOrder',
  importKey: 'code',
  card: { title: 'name', subtitle: 'code', meta: ['daysPerYear'], status: 'status' },
})

/** One kind of leave as the helpers read it */
export type LeaveTypeValues = Pick<
  z.infer<typeof leaveType.schema>,
  | 'paid'
  | 'daysPerYear'
  | 'calendarDays'
  | 'minServiceDays'
  | 'prorate'
  | 'documentFromDays'
  | 'halfDay'
  | 'noticeDays'
  | 'allowPast'
  | 'keepsDiligence'
>

/**
 * The leave the Labour Protection Act gives, as the starting set (HR edits them). Their colours stay off red and amber,
 * which ตารางเวลาทำงาน gives absence and lateness
 */
export const standardLeaveTypes: (Pick<z.infer<typeof leaveType.schema>, 'code' | 'name' | 'icon' | 'color'> & Partial<LeaveTypeValues>)[] =
  [
    { code: 'SICK', name: 'ลาป่วย', icon: 'firstAid', color: 'teal', daysPerYear: 30, documentFromDays: 3, allowPast: true },
    { code: 'PERSONAL', name: 'ลากิจ', icon: 'briefcase', color: 'violet', daysPerYear: 3, noticeDays: 1 },
    {
      code: 'ANNUAL',
      name: 'ลาพักร้อน',
      icon: 'beach',
      color: 'cyan',
      daysPerYear: 6,
      minServiceDays: 365,
      prorate: true,
      noticeDays: 3,
      keepsDiligence: true,
    },
    {
      code: 'MATERNITY',
      name: 'ลาคลอด',
      icon: 'baby',
      color: 'pink',
      daysPerYear: 98,
      calendarDays: true,
      halfDay: false,
      documentFromDays: 1,
    },
    { code: 'MILITARY', name: 'ลารับราชการทหาร', icon: 'shield', color: 'green', daysPerYear: 60, halfDay: false, documentFromDays: 1 },
    { code: 'UNPAID', name: 'ลาไม่รับค่าจ้าง', icon: 'cashOff', color: 'grey', paid: false, daysPerYear: null, noticeDays: 1 },
  ]

/**
 * hr.leaveAllowance (สิทธิ์การลา): one person's days of one kind in one year, when they differ
 * from the kind's — more or fewer days, days carried over from last year. None: the kind's days.
 */
export const leaveAllowance = defineResource({
  app: 'hr',
  name: 'leaveAllowance',
  path: 'leave-allowances',
  schema: z.object({
    employeeId: z.string().min(1),
    leaveTypeId: z.string().min(1),
    year: z.number().int().min(2000).max(2100),
    /** Days this year instead of the kind's; empty: the kind's */
    days: z.number().min(0).max(366).nullish(),
    carriedOver: z.number().min(0).max(366).default(0),
    /** Taken this year before the system was used (brought forward), counted as used */
    usedBefore: z.number().min(0).max(366).default(0),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    employeeId: { type: 'ref', ref: { resource: 'hr.employee' }, filter: true, list: { priority: 1 }, form: { span: 6 } },
    leaveTypeId: {
      type: 'ref',
      ref: { resource: 'hr.leaveType', where: { status: 'active' } },
      filter: true,
      list: { priority: 1 },
      form: { span: 6 },
    },
    year: { type: 'integer', filter: true, list: { width: 100, priority: 1 }, form: { span: 4 } },
    days: { list: { width: 110, priority: 1 }, form: { span: 4 } },
    carriedOver: { list: { width: 110, priority: 2 }, form: { span: 4 } },
    usedBefore: { list: { width: 110, priority: 2 }, form: { span: 4 } },
    /** The employee's branch (server-set), for a lead's scope */
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 3, hidden: true } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'employeeId',
  columns: ['employeeId', 'leaveTypeId', 'year', 'days', 'carriedOver', 'usedBefore', 'branchId'],
  defaultSort: '-year',
  branchFields: ['branchId'],
})

export const leaveHalves = ['full', 'morning', 'afternoon'] as const
export type LeaveHalf = (typeof leaveHalves)[number]
export const leaveStatuses = ['pending', 'approved', 'rejected', 'cancelled'] as const

/**
 * hr.leave (ใบลา): pending until the person's manager (hr.employee.managerId), their branch's
 * lead or HR approves or rejects it (with a reason) — `approve`, never by the person themselves.
 * Its days are counted by the server: working days of the person's schedule, holidays out (all
 * days for a calendar-day kind), half a day for a half-day leave. The person cancels their own
 * before it starts; after that, HR or a lead. `recordFor`: a lead writes one for someone without
 * a phone (they sign the printed slip).
 */
export const leave = defineResource({
  app: 'hr',
  name: 'leave',
  path: 'leaves',
  schema: z.object({
    employeeId: z.string().min(1),
    leaveTypeId: z.string().min(1),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    /** One day only: the whole day, or its morning or afternoon */
    half: z.enum(leaveHalves).default('full'),
    reason: z.string().trim().max(500).nullish(),
    /** A medical certificate, a letter… (file ids) */
    attachments: z.array(z.string()).default([]),
  }),
  fields: {
    employeeId: { type: 'ref', ref: { resource: 'hr.employee' }, filter: true, list: { priority: 1 }, form: { span: 12, custom: true } },
    /** Picked from chips in each kind's icon and colour */
    leaveTypeId: {
      type: 'ref',
      ref: { resource: 'hr.leaveType', where: { status: 'active' } },
      filter: true,
      list: { priority: 1 },
      form: { span: 12, custom: true },
    },
    startDate: { filter: true, list: { width: 130, priority: 1 }, form: { span: 4 } },
    endDate: { list: { width: 130, priority: 1 }, form: { span: 4 } },
    half: { list: false, form: { span: 4, custom: true } },
    reason: { type: 'textarea', list: { priority: 2 }, form: { span: 12 } },
    attachments: { type: 'json', list: false, form: { span: 12, custom: true }, import: false, export: false },
    /** Days it takes (server-counted) */
    days: { readOnly: true, type: 'number', list: { width: 90, priority: 1 } },
    status: {
      readOnly: true,
      type: 'select',
      options: leaveStatuses,
      filter: true,
      tones: { pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'neutral' },
      list: { width: 120, priority: 1 },
    },
    /** "ลาป่วย 2 วัน · มาลี ตัวอย่าง" (server-made): its name wherever it is shown */
    summary: { readOnly: true, search: true, list: false },
    userId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: false },
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 2 } },
    requestedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    requestedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    reviewedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    reviewedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    rejectReason: { readOnly: true, list: false },
  },
  title: 'summary',
  columns: ['startDate', 'employeeId', 'leaveTypeId', 'endDate', 'days', 'status', 'branchId', 'reason', 'requestedAt', 'reviewedByUserId'],
  defaultSort: '-startDate',
  actions: ['read', 'create', 'update', 'delete', 'export', 'print', 'approve', 'cancel', 'recordFor'],
  ownerField: ['userId', 'requestedByUserId'],
  branchFields: ['branchId'],
  card: { title: 'summary', subtitle: 'leaveTypeId', meta: ['startDate', 'days'], status: 'status' },
})

export interface LeaveDates {
  startDate: string
  endDate: string
  half?: LeaveHalf
}

/** What is wrong with the dates of a leave of this kind, or null (the server says the same) */
export function leaveProblem(leaveDates: LeaveDates, type: Pick<LeaveTypeValues, 'halfDay'>): { field: string; key: string } | null {
  if (leaveDates.endDate < leaveDates.startDate) return { field: 'endDate', key: 'endBeforeStart' }
  const half = leaveDates.half ?? 'full'
  if (half !== 'full' && leaveDates.endDate !== leaveDates.startDate) return { field: 'half', key: 'halfOneDay' }
  if (half !== 'full' && !type.halfDay) return { field: 'half', key: 'noHalfDay' }
  return null
}

/**
 * Days a leave takes: the person's working days from start to end (`isWorking`), or every day
 * for a calendar-day kind; half a day when it is the morning or afternoon of a working day.
 */
export function leaveDays(leaveDates: LeaveDates, isWorking: (date: string) => boolean, calendarDays = false): number {
  if (leaveDates.endDate < leaveDates.startDate) return 0
  if ((leaveDates.half ?? 'full') !== 'full') return calendarDays || isWorking(leaveDates.startDate) ? 0.5 : 0
  let days = 0
  for (let date = leaveDates.startDate; date <= leaveDates.endDate; date = addDays(date, 1)) {
    if (calendarDays || isWorking(date)) days += 1
  }
  return days
}

export interface LeaveBalance {
  /** Days this year (the person's allowance, else the kind's when they qualify); null: no limit */
  entitled: number | null
  carriedOver: number
  /** Approved */
  used: number
  /** Waiting for approval */
  pending: number
  /** What is left to ask for; null: no limit */
  remaining: number | null
  /** Has served long enough (annual leave) */
  eligible: boolean
}

/**
 * Months of a year left after someone qualifies on `date` (YYYY-MM-DD): the rest of the year,
 * from the next month — or from that month when it is its first day
 */
function monthsLeft(date: string): number {
  const month = Number(date.slice(5, 7))
  const day = Number(date.slice(8, 10))
  return 12 - month + (day === 1 ? 1 : 0)
}

/** One person's days of one kind in one year (the year of `asOf`) */
export function leaveBalance(input: {
  type: Pick<LeaveTypeValues, 'daysPerYear' | 'minServiceDays'> & { prorate?: boolean | null }
  allowance?: { days?: number | null; carriedOver?: number | null; usedBefore?: number | null } | null
  /** Their start date (hr.employee.startDate); unknown counts as qualified */
  startDate?: string | null
  /** The day the leave would start (service is counted to it) */
  asOf: string
  used: number
  pending: number
}): LeaveBalance {
  const { type, allowance, startDate, asOf, pending } = input
  // Leave taken before the system counts as taken
  const used = input.used + (allowance?.usedBefore ?? 0)
  const served = startDate ? Math.round((Date.parse(`${asOf}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86_400_000) : Infinity
  const eligible = served >= (type.minServiceDays ?? 0)
  const carriedOver = allowance?.carriedOver ?? 0
  // The day they qualify: in this year, a prorated kind gives only the months left, by half days
  const qualifies = startDate && type.minServiceDays ? addDays(startDate, type.minServiceDays) : null
  const firstYear = !!type.prorate && !!qualifies && qualifies.slice(0, 4) === asOf.slice(0, 4) && qualifies.slice(5) !== '01-01'
  const yearly =
    type.daysPerYear == null
      ? null
      : firstYear
        ? Math.floor(((type.daysPerYear * monthsLeft(qualifies as string)) / 12) * 2) / 2
        : type.daysPerYear
  const entitled = allowance?.days ?? (yearly == null ? null : eligible ? yearly : 0)
  const remaining = entitled == null ? null : Math.max(0, entitled + carriedOver - used - pending)
  return { entitled, carriedOver, used, pending, remaining, eligible }
}

/** Whether a leave of this many days needs a document (a medical certificate) */
export const needsDocument = (type: Pick<LeaveTypeValues, 'documentFromDays'>, days: number) =>
  type.documentFromDays != null && days >= type.documentFromDays
